
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, CheckCircle, Shield, Mail, Phone } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LocationState, InsuranceRecommendation } from "@/types/forms";
import StickyNavigation from "@/components/layout/StickyNavigation";
import TenantFooter from "@/components/layout/TenantFooter";

const insuranceTypes = {
  property: "Property All Risks",
  liability: "Third Party Liability", 
  workers: "Workers Compensation",
  cyber: "Cyber Insurance",
  fleet: "Fleet Insurance",
  professional: "Professional Indemnity",
  interruption: "Business Interruption"
};

const Confirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedProducts = [], mandatoryCovers = [], optionalCovers = [] } = (location.state as LocationState) || {};

  // If no products are selected, redirect to decision tree (tenant-aware)
  const pathMatch = location.pathname.match(/^(?:\/business)?\/site\/([^/]+)/);
  const subPrefix = pathMatch ? `/site/${pathMatch[1]}` : '';
  if (selectedProducts.length === 0 && mandatoryCovers.length === 0) {
    navigate(`${subPrefix}/decision-tree`);
    return null;
  }

  const selectedRecommendations = optionalCovers.filter((rec: InsuranceRecommendation) =>
    selectedProducts.includes(rec.type)
  );

  const handleGetQuotes = () => {
    // Navigate to multi-product quote form (tenant-aware)
    navigate(`${subPrefix}/multi-quote`, {
      state: {
        selectedProducts: Array.from(new Set([...mandatoryCovers, ...selectedProducts])),
        mandatoryCovers,
        optionalCovers: selectedRecommendations
      }
    });
  };

  const handleBackToAssessment = () => {
    // Navigate back to decision tree with state to show Assessment Complete page (tenant-aware)
    navigate(`${subPrefix}/decision-tree`, {
      state: {
        showAssessmentComplete: true,
        mandatoryCovers,
        optionalCovers,
        selectedProducts
      }
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <StickyNavigation />
      <div className="py-4 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <div className="mb-4">
            <button
              onClick={handleBackToAssessment}
              className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              <span>Back to Assessment</span>
            </button>
          </div>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Confirmation
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            You've selected {selectedRecommendations.length + mandatoryCovers.length} insurance products. Review your selection and get your personalized quotes.
          </p>
        </div>

        {/* Selected Products */}
        <div className="space-y-4 mb-12">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Your Selected Insurance Products</h2>
          
          {/* Mandatory Covers */}
          {mandatoryCovers.map((coverType: string) => (
            <Card key={coverType} className="border border-gray-200">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                      <Shield className="h-5 w-5 text-red-600" />
                    </div>
                    <CardTitle className="text-xl text-gray-900">
                      {coverType === "employee-medical" && "Employee Medical Insurance"}
                      {coverType === "workmens-comp" && "Workers Compensation"}
                      {coverType === "public-liability" && "Public Liability"}
                      {coverType === "property-all-risks" && "Property All Risks"}
                    </CardTitle>
                  </div>
                  <Badge variant="secondary" className="bg-red-100 text-red-800">
                    UAE Mandatory
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Essential protection required by UAE law for your business.
                </p>
              </CardContent>
            </Card>
          ))}

          {/* Optional Covers */}
          {selectedRecommendations.map((rec: InsuranceRecommendation) => (
            <Card key={rec.type} className="border border-gray-200">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Shield className="h-5 w-5 text-blue-600" />
                    </div>
                    <CardTitle className="text-xl text-gray-900">{rec.name}</CardTitle>
                  </div>
                  <Badge 
                    variant={rec.priority === "Recommended" ? "default" : "secondary"}
                    className={rec.priority === "Recommended" ? "bg-orange-100 text-orange-800" : ""}
                  >
                    {rec.priority}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Essential protection for your business based on your specific risk profile.
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Next Steps */}
        <Card className="border border-gray-200 mb-8">
          <CardContent className="pt-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">What Happens Next?</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-blue-600 text-sm font-semibold">1</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Complete Quote Form</h4>
                  <p className="text-gray-600">Answer questions specific to your selected insurance products.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-blue-600 text-sm font-semibold">2</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Expert Consultation</h4>
                  <p className="text-gray-600">Our insurance specialists will review your needs and customize your coverage.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-blue-600 text-sm font-semibold">3</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Get Protected</h4>
                  <p className="text-gray-600">Choose your preferred options and activate your business insurance.</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="text-center space-y-4">
          <Button size="lg" className="w-full sm:w-auto" onClick={handleGetQuotes}>
            Continue to Quote
          </Button>
          <div className="text-gray-600">
            <p className="mb-2">Or contact us directly:</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a href="mailto:business@yallacompare.com" className="flex items-center gap-2 text-blue-600 hover:text-blue-700">
                <Mail className="h-4 w-4" />
                business@yallacompare.com
              </a>
              <a href="tel:+971586948688" className="flex items-center gap-2 text-blue-600 hover:text-blue-700">
                <Phone className="h-4 w-4" />
                +971586948688
              </a>
            </div>
          </div>
        </div>
        </div>
      </div>
      <TenantFooter />
    </div>
  );
};

export default Confirmation;
